import React, { useState, useEffect } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Profile } from "@/pages/users/types";
import { AdminTeamMemberCard } from "./admin-team/components/AdminTeamMemberCard";
import { Users } from "lucide-react";

interface AdminTeamMember extends Profile {
  member_type: string;
  team_ids: string[];
}

const AdminTeamMembers = () => {
  const [adminUsers, setAdminUsers] = useState<AdminTeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserId, setCurrentUserId] = useState<string>("");
  const { toast } = useToast();

  const fetchAdministrationUsers = async () => {
    try {
      setLoading(true);
      
      // Get current user's ID
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAdminUsers([]);
        return;
      }

      setCurrentUserId(user.id);

      // Fetch admin team members for the current admin
      const { data: adminTeam, error: teamError } = await supabase
        .from("admin_team")
        .select("member_id, member_type, team_ids")
        .eq("admin_id", user.id);

      if (teamError) throw teamError;

      const memberIds = adminTeam?.map(m => m.member_id) || [];
      
      if (memberIds.length === 0) {
        setAdminUsers([]);
        return;
      }

      // Create maps for member_type and team_ids
      const memberTypeMap = new Map(
        adminTeam?.map(at => [at.member_id, at.member_type]) || []
      );
      const teamIdsMap = new Map(
        adminTeam?.map(at => [at.member_id, at.team_ids || []]) || []
      );

      // Fetch profiles for all team members
      const { data: profiles, error: profilesError } = await supabase
        .from("profiles")
        .select(`
          id,
          full_name,
          email,
          user_roles!inner(role)
        `)
        .in("id", memberIds);

      if (profilesError) throw profilesError;

      // Map to Profile type with member_type and team_ids
      const mappedProfiles: AdminTeamMember[] = (profiles || []).map((profile: any) => ({
        id: profile.id,
        full_name: profile.full_name,
        email: profile.email,
        member_type: memberTypeMap.get(profile.id) || "manager",
        team_ids: teamIdsMap.get(profile.id) || [],
        user_roles: profile.user_roles.filter((ur: any) => 
          ["admin", "super_admin", "manager", "guard", "client"].includes(ur.role)
        )
      }));

      setAdminUsers(mappedProfiles);
    } catch (error: any) {
      console.error("Error fetching administration users:", error);
      toast({
        title: "Error",
        description: `Failed to load Administration team: ${error.message}`,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdministrationUsers();
  }, []);

  return (
    <AppLayout>
      <div className="container mx-auto p-4 sm:p-6 space-y-6">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Administration Team</h1>
          <p className="text-muted-foreground">
            Administrators, managers, and contributors
          </p>
        </div>

        {loading ? (
          <Card className="p-8">
            <div className="flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <p className="text-sm text-muted-foreground">Loading administration team...</p>
              </div>
            </div>
          </Card>
        ) : adminUsers.length > 0 ? (
          <>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>Total members: <strong className="text-foreground">{adminUsers.length}</strong></span>
            </div>
            
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
              {adminUsers.map((member) => (
                <AdminTeamMemberCard
                  key={member.id}
                  member={member}
                  memberType={member.member_type}
                  teamIds={member.team_ids}
                  currentUserId={currentUserId}
                  onUpdate={fetchAdministrationUsers}
                />
              ))}
            </div>
          </>
        ) : (
          <Card className="p-12">
            <div className="flex flex-col items-center justify-center text-center gap-2">
              <Users className="h-12 w-12 text-muted-foreground/50" />
              <p className="text-muted-foreground">No administration team members found</p>
            </div>
          </Card>
        )}
      </div>
    </AppLayout>
  );
};

export default AdminTeamMembers;
